import axios from "axios";

import { uploadFile } from "~/api/modules/Common";
import BaseApi, { ApiError } from "~/api/modules/BaseApi";
import {
  TaxRegistration,
  TaxRegistrationQuestions,
  TaxRegistrationStatus,
} from "./types";

export interface UpsertTaxRegistrationRequest {
  id?: string;
  email: string;
  questions: TaxRegistrationQuestions;
}

export interface GetTaxRegistrationsResponse {
  count: number;
  taxRegistrations: TaxRegistration[];
}

export interface TaxRegistrationProcessResponse {
  requestPayload: TaxRegistrationQuestions;
  requestXml: string;
  pdf: string;
  processResult: string;
  message: string;
}

/**
 * The presigned POST kontax-backend answers with (SMT-1341). `formData` carries
 * the fields S3 expects next to the file, in the order S3 expects them, so it
 * is appended to the FormData before the file and never rebuilt here.
 */
export interface TaxRegistrationAttachmentUpload {
  attachmentId: string;
  key: string;
  url: string;
  formData: { key: string; value: string }[];
}

interface GetTaxRegistrationsOptions {
  limit?: number;
  offset?: number;
  status?: TaxRegistrationStatus;
  withTaxRegistration?: boolean;
}

class TaxRegistrationApi extends BaseApi {
  protected get moduleUrl(): string {
    return "/kontax/tax-registrations";
  }

  public getTaxRegistrations = async (
    email?: string,
    userIds?: number[],
    options: GetTaxRegistrationsOptions = {}
  ): Promise<GetTaxRegistrationsResponse> => {
    const { data } = await this.get<GetTaxRegistrationsResponse>("", {
      params: {
        ...(email ? { email } : {}),
        ...(!options.withTaxRegistration && userIds?.length
          ? { userIds: userIds.join(","), limit: userIds.length, offset: 0 }
          : {}),
        ...options,
      },
      skipNotification: true,
    });
    return data;
  };

  public upsertTaxRegistration = async (
    payload: UpsertTaxRegistrationRequest
  ): Promise<TaxRegistration> => {
    const { data } = await this.put<TaxRegistration>("", payload, {
      skipNotification: true,
    });
    return data;
  };

  public getTaxRegistrationDraft = async (
    id: string
  ): Promise<TaxRegistrationProcessResponse> => {
    const { data } = await this.post<TaxRegistrationProcessResponse>(
      `/${id}/draft`,
      {},
      { skipNotification: true }
    );
    return data;
  };

  public submitTaxRegistration = async (
    id: string
  ): Promise<TaxRegistrationProcessResponse> => {
    const { data } = await this.post<TaxRegistrationProcessResponse>(
      `/${id}/submit`,
      {},
      { skipNotification: true }
    );
    return data;
  };

  public createAttachment = async (
    id: string,
    payload: { fileName: string; size: number }
  ): Promise<TaxRegistrationAttachmentUpload> => {
    const { data } = await this.post<TaxRegistrationAttachmentUpload>(
      `/${id}/attachments`,
      payload,
      { skipNotification: true }
    );
    return data;
  };

  public acknowledgeAttachment = async (
    id: string,
    attachmentId: string,
    payload: { key: string; fileName: string }
  ): Promise<TaxRegistration> => {
    const { data } = await this.post<TaxRegistration>(
      `/${id}/attachments/${attachmentId}/acknowledge`,
      payload,
      { skipNotification: true }
    );
    return data;
  };

  public deleteAttachment = async (
    id: string,
    attachmentId: string
  ): Promise<TaxRegistration> => {
    const { data } = await this.delete<TaxRegistration>(
      `/${id}/attachments/${attachmentId}`,
      { skipNotification: true }
    );
    return data;
  };

  public getAttachmentUrl = async (
    id: string,
    attachmentId: string
  ): Promise<{ url: string }> => {
    const { data } = await this.get<{ url: string }>(
      `/${id}/attachments/${attachmentId}/url`,
      { skipNotification: true }
    );
    return data;
  };
}

export const taxRegistrationApi = new TaxRegistrationApi();

/**
 * The backend's message, whatever threw. `BaseApi` has already turned its own
 * failures into an `Error` carrying `response.data.message`; the S3 POST goes
 * out on a bare axios instance, so its rejection is unwrapped here. A limit is
 * only useful to the operator if the sentence naming it survives all three
 * hops (presign, S3, acknowledge).
 */
const toAttachmentError = (error: unknown): Error => {
  if (axios.isAxiosError(error)) {
    return new Error(error.response?.data?.message || error.message);
  }
  return error instanceof Error ? error : new Error(String(error));
};

/** How often an acknowledge whose answer never arrived is repeated. */
const ACKNOWLEDGE_RETRIES = 2;

/**
 * An acknowledge can commit while its answer is lost on the way back. Repeating
 * it with the same id and key is safe, since kontax-backend answers a repeated
 * one with the record as it stands; uploading again would mint a new id and
 * store the same PDF twice. A request the backend did answer is never repeated:
 * its refusal names a limit the operator has to see.
 */
const acknowledgeWithRetry = async (
  ...args: Parameters<TaxRegistrationApi["acknowledgeAttachment"]>
): Promise<TaxRegistration> => {
  for (let attempt = 0; ; attempt += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return await taxRegistrationApi.acknowledgeAttachment(...args);
    } catch (error) {
      const isAnswerLost = error instanceof ApiError && error.isResponseMissing;
      if (!isAnswerLost || attempt >= ACKNOWLEDGE_RETRIES) throw error;
    }
  }
};

/**
 * Presign, upload to S3, acknowledge — the three hops of the existing asset
 * upload (`createTransactionAsset`), for a tax registration attachment. The
 * acknowledged registration comes back with the attachment already in
 * `attachments`, so the caller never has to guess what the record now holds.
 */
export const uploadTaxRegistrationAttachment = async (
  id: string,
  file: File
): Promise<TaxRegistration> => {
  try {
    const { attachmentId, key, url, formData } =
      await taxRegistrationApi.createAttachment(id, {
        fileName: file.name,
        size: file.size,
      });

    const form = new FormData();
    formData.forEach((field) => form.append(field.key, field.value));
    /*
     * The presigned policy carries `["starts-with", "$Content-Type",
     * "application/pdf"]`, so S3 refuses a POST that does not declare it. It
     * goes after the policy fields and before the file, because S3 ignores
     * every field that follows the file part.
     */
    form.append("Content-Type", "application/pdf");
    form.append("file", file);
    await uploadFile({ url, form });

    // The key only carries a sanitised name; the record keeps the operator's
    // own spelling, which is what the Anlagen list and the preview show.
    return await acknowledgeWithRetry(id, attachmentId, {
      key,
      fileName: file.name,
    });
  } catch (error) {
    throw toAttachmentError(error);
  }
};
